import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import toolkitService from '../services/ToolkitService';
import Button from './shared/Button';
import ToolkitBatchPage from './toolkit/ToolkitBatchPage';

// Sync › project › Toolkit › Create Rooms — ported from the desktop's Scripts › Create Rooms. Creates a range of rooms
// of one room type in Opera Cloud (toolkit property.js create-room). The range is numbers with an optional letter
// prefix (101–120, PM001–PM010), at most 500 rooms; rooms that already exist for the type are listed as skipped
// rather than sent again.

const COLUMNS = [
    { key: 'roomNumber', label: 'Room' },
    { key: 'roomType', label: 'Room type' },
    { key: 'floor', label: 'Floor' },
    { key: 'result', label: 'Result' },
];

const parseRoom = (v) => {
    const m = String(v).trim().match(/^([A-Za-z]*)(\d+)$/);
    return m ? { prefix: m[1], number: parseInt(m[2], 10), pad: m[2].length } : null;
};
const roomRange = (from, to) => {
    const f = parseRoom(from);
    const t = parseRoom(to);
    if (!f || !t) throw new Error('Room numbers must be numeric or alphanumeric (e.g. 101 or PM001).');
    if (f.prefix !== t.prefix) throw new Error('From and To must share the same alphabetic prefix.');
    if (f.number > t.number) throw new Error('From must be less than or equal to To.');
    if (t.number - f.number > 499) throw new Error('Range exceeds 500 rooms. Please use a smaller range.');
    const pad = Math.max(f.pad, t.pad);
    return Array.from({ length: t.number - f.number + 1 }, (_, i) => `${f.prefix}${String(f.number + i).padStart(pad, '0')}`);
};

function ToolkitCreateRoomsPage() {
    const [types, setTypes] = useState([]);
    const [typesMsg, setTypesMsg] = useState('');
    const [typeCode, setTypeCode] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [floor, setFloor] = useState('');
    const type = types.find((t) => t.code === typeCode);

    const loadTypes = async (ctx) => {
        setTypesMsg('Loading room types…');
        try {
            const items = (await toolkitService.property(ctx, 'list-room-types')).data.result.items;
            setTypes(items);
            setTypesMsg(`${items.length} room type(s).`);
        } catch (err) {
            setTypesMsg(err?.response?.data?.message || err.message);
        }
    };

    // The rows: the requested range, with rooms already configured for the type marked as skipped up front.
    const fetchRows = async (ctx) => {
        const rooms = roomRange(from, to);
        const existing = new Set((await toolkitService.property(ctx, 'list-rooms-by-type', { roomType: typeCode })).data.result.items.map(String));
        return rooms.map((roomNumber) => ({
            roomNumber, roomType: typeCode, floor,
            ...(existing.has(roomNumber) ? { status: 'skipped', errorMessage: `Room ${roomNumber} already exists` } : {}),
        }));
    };
    const request = (row) => ({ area: 'property', op: 'create-room', args: {
        roomNumber: row.roomNumber, roomType: row.roomType, floor: row.floor || null, description: type?.description || null,
    } });

    return (
        <ToolkitBatchPage title="Create Rooms"
            subtitle="Create a range of rooms of one room type in Opera Cloud."
            queryTitle="Rooms to create" queryLabel="Preview" fetchRows={fetchRows} canQuery={!!(typeCode && from && to)}
            queryForm={(ctx) => (
                <>
                    <Button variant="secondary" onClick={() => loadTypes(ctx)}><RefreshCw className="icon" /> Load room types</Button>
                    <label>
                        <span>Room type {typesMsg && <em className="tkb-desc">({typesMsg})</em>}</span>
                        <select value={typeCode} onChange={(e) => setTypeCode(e.target.value)} disabled={!types.length}>
                            <option value="">{types.length ? 'Choose…' : 'Load room types first'}</option>
                            {types.map((t) => <option key={t.code} value={t.code}>{t.code}{t.description ? ` — ${t.description}` : ''}{t.pseudo ? ' (pseudo)' : ''}</option>)}
                        </select>
                    </label>
                    <label><span>From room</span><input type="text" value={from} onChange={(e) => setFrom(e.target.value.toUpperCase())} style={{ width: 110 }} /></label>
                    <label><span>To room</span><input type="text" value={to} onChange={(e) => setTo(e.target.value.toUpperCase())} style={{ width: 110 }} /></label>
                    <label><span>Floor</span><input type="text" value={floor} onChange={(e) => setFloor(e.target.value)} style={{ width: 80 }} /></label>
                </>
            )}
            columns={COLUMNS} searchKeys={['roomNumber']} exportName="Create_Rooms"
            request={request} server={{ screen: 'create-rooms', title: 'Create Rooms', keyOf: (row) => row.roomNumber }} />
    );
}

export default ToolkitCreateRoomsPage;
